import { Check } from 'lucide-react';
import { useId, type ComponentPropsWithRef, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

export interface CheckboxProps extends Omit<ComponentPropsWithRef<'input'>, 'type' | 'children'> {
  /** Etykieta obok pola wyboru. */
  label?: ReactNode;
  /** Opis pod etykieta (np. „Umożliw wodowanie szczęśliwych przegranych”). */
  description?: ReactNode;
}

/**
 * Pole wyboru w stylu makiet — zaznaczone wypelnia sie kolorem marki.
 *
 * Uzywamy natywnego `<input type="checkbox">` z `appearance-none`, wiec
 * zachowujemy pelna obsluge klawiatury i czytnikow ekranu bez dodatkowego kodu.
 */
export function Checkbox({ className, label, description, id, disabled, ...props }: CheckboxProps) {
  const generatedId = useId();
  const controlId = id ?? generatedId;

  const control = (
    <span className="relative inline-grid shrink-0 place-items-center">
      <input
        id={controlId}
        type="checkbox"
        disabled={disabled}
        className={cn(
          'peer size-[1.125rem] appearance-none rounded-[0.3125rem]',
          'border border-border-strong bg-surface-raised',
          'transition-colors duration-150',
          'checked:border-brand checked:bg-brand',
          'focus-visible:ring-brand/35 focus-visible:ring-2 focus-visible:ring-offset-2',
          'focus-visible:ring-offset-canvas focus-visible:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-45',
          className,
        )}
        {...props}
      />
      <Check
        aria-hidden="true"
        strokeWidth={3.5}
        className="text-fg-on-brand pointer-events-none absolute size-3 opacity-0 peer-checked:opacity-100"
      />
    </span>
  );

  if (!label && !description) {
    return control;
  }

  return (
    <div className={cn('flex items-start gap-2.5', disabled && 'opacity-60')}>
      <span className="flex h-5 items-center">{control}</span>
      <div className="flex flex-col gap-0.5">
        {label && (
          <label
            htmlFor={controlId}
            className={cn(
              'text-fg text-sm font-medium',
              disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            )}
          >
            {label}
          </label>
        )}
        {description && <p className="text-fg-subtle text-xs">{description}</p>}
      </div>
    </div>
  );
}
